import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, MapPinned, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARCHETYPE_LABEL = {
  leon: "LEÓN",
  lobo: "LOBO",
  halcon: "HALCÓN",
  delfin: "DELFÍN",
};

/**
 * Admin · Solicitudes (Sprint 3)
 *
 * Lists entitlement requests (status="requested") and allows the admin
 * to activate them. Activation triggers a mandatory user notification
 * email handled by the backend.
 */
export default function SolicitudesPanel({ password }) {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("requested"); // requested | active | all
  const [data, setData] = useState(null);
  const [activating, setActivating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/entitlements?status=${filter}`,
        { headers: { "X-Admin-Password": password } },
      );
      setData(data);
    } catch {
      toast.error("No pudimos cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  }, [filter, password]);

  useEffect(() => {
    load();
  }, [load]);

  const activate = async (entId) => {
    setActivating(entId);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/entitlements/${entId}/activate`,
        {},
        { headers: { "X-Admin-Password": password } },
      );
      if (res?.email?.sent) {
        toast.success("Mapa activado y notificado al usuario.");
      } else {
        toast.success("Mapa activado (email no enviado — revisa Resend).");
      }
      load();
    } catch {
      toast.error("No pudimos activar el entitlement");
    } finally {
      setActivating(null);
    }
  };

  const rows = data?.entitlements || [];

  return (
    <div data-testid="admin-solicitudes-panel" className="space-y-6">
      {/* Filter pills */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 mr-2">
          Estado
        </span>
        {["requested", "active", "all"].map((f) => (
          <button
            key={f}
            data-testid={`solicitudes-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              filter === f
                ? "border-[#29BC9B] text-[#29BC9B] bg-[#29BC9B]/5"
                : "border-white/10 text-white/50 hover:border-white/30"
            }`}
          >
            {f === "requested"
              ? "Pendientes"
              : f === "active"
              ? "Activos"
              : "Todos"}
          </button>
        ))}
        <span className="text-xs text-white/40 ml-auto">
          {rows.length} {rows.length === 1 ? "solicitud" : "solicitudes"}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-md border border-white/8 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="w-full text-sm"
            data-testid="solicitudes-table"
          >
            <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
              <tr>
                <th className="text-left px-4 py-3">Solicitante</th>
                <th className="text-left px-4 py-3">Combinación</th>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Producto</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Solicitado</th>
                <th className="text-left px-4 py-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-white/40">
                    <Loader2 className="inline animate-spin mr-2" size={14} />
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-white/40">
                    {filter === "requested"
                      ? "Sin solicitudes pendientes."
                      : filter === "active"
                      ? "Sin activaciones aún."
                      : "Sin solicitudes."}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <SolicitudRow
                    key={r.id}
                    row={r}
                    activating={activating === r.id}
                    onActivate={() => activate(r.id)}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SolicitudRow({ row, activating, onActivate }) {
  const lead = row.lead || {};
  const isRequested = row.status === "requested";
  const isActive = row.status === "active";
  const combo = lead.archetype
    ? `${ARCHETYPE_LABEL[lead.archetype] || lead.archetype}` +
      (lead.secondary ? ` + ${ARCHETYPE_LABEL[lead.secondary] || lead.secondary}` : "")
    : "—";
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02]">
      <td className="px-4 py-3" data-testid={`solicitud-lead-${row.id}`}>
        <div className="text-white">{lead.name || "—"}</div>
        <div className="text-xs text-white/50">{lead.email || "—"}</div>
      </td>
      <td className="px-4 py-3 text-xs text-white/70">{combo}</td>
      <td className="px-4 py-3 text-xs">
        {lead.company_code ? (
          <div className="flex flex-col gap-0.5">
            <span className="text-white/80">{lead.company_name}</span>
            <span className="text-[9px] font-mono tracking-wider text-[#29BC9B]/80">
              {lead.company_code}
            </span>
          </div>
        ) : (
          <span className="text-white/30">Individual</span>
        )}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase text-[#29BC9B] bg-[#29BC9B]/10 px-2 py-1 rounded-sm">
          <MapPinned size={11} />
          {row.product === "map" ? "Mapa · USD 7" : row.product}
        </span>
      </td>
      <td className="px-4 py-3">
        <StatusPill status={row.status} />
      </td>
      <td className="px-4 py-3 text-xs text-white/40 tabular-nums">
        {row.requested_at
          ? new Date(row.requested_at).toLocaleString("es")
          : new Date(row.created_at).toLocaleString("es")}
      </td>
      <td className="px-4 py-3">
        {isRequested ? (
          <button
            data-testid={`solicitud-activate-${row.id}`}
            onClick={onActivate}
            disabled={activating}
            className="inline-flex items-center gap-2 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[10px] tracking-[0.2em] uppercase font-bold px-3 py-2 rounded-sm transition-colors disabled:opacity-50"
          >
            {activating ? (
              <Loader2 className="animate-spin" size={12} />
            ) : (
              <CheckCircle2 size={12} />
            )}
            Activar Mapa
          </button>
        ) : isActive ? (
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#29BC9B]">
            <Mail size={11} />
            Notificado
          </div>
        ) : (
          <span className="text-white/30 text-xs">—</span>
        )}
      </td>
    </tr>
  );
}

function StatusPill({ status }) {
  const map = {
    requested: { label: "Pendiente", cls: "text-amber-300 bg-amber-300/10" },
    active: { label: "Activo", cls: "text-[#29BC9B] bg-[#29BC9B]/10" },
    revoked: { label: "Revocado", cls: "text-red-400 bg-red-400/10" },
  };
  const m = map[status] || { label: status, cls: "text-white/60 bg-white/10" };
  return (
    <span
      className={`text-[9px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm ${m.cls}`}
    >
      {m.label}
    </span>
  );
}
